import { useCallback, useEffect, useState } from "react"
import {
  Alert,
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material"
import type { SelectChangeEvent } from "@mui/material"
import { ExamSectionSkeleton } from "./ExamSectionSkeleton"
import { Title } from "components/Common/Title"
import analyticsService from "services/analyticsService"
import type { IAnalyticsExamDuration } from "types/analyticsTypes"

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60)
  const s = Math.round(seconds % 60)
  return `${m}m ${s.toString().padStart(2, "0")}s`
}

const STATUS_OPTIONS = [
  { label: "Completed", value: "Completed" },
  { label: "Quit", value: "Quit" },
  { label: "Both", value: "" },
]

type Props = {
  orgId: string | null
}

export function ExamDurationSection({ orgId }: Props) {
  const [data, setData] = useState<IAnalyticsExamDuration[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [status, setStatus] = useState<string>("")

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const result = await analyticsService.getExamsDuration(orgId, status)
      setData(result)
    } catch {
      setError("Failed to load exam duration data.")
    } finally {
      setLoading(false)
    }
  }, [orgId, status])

  useEffect(() => {
    load()
  }, [load])

  const handleStatusChange = (event: SelectChangeEvent<string>) => {
    setStatus(event.target.value)
  }

  if (loading) return <ExamSectionSkeleton />

  if (error) {
    return (
      <Paper sx={{ p: 3 }}>
        <Title>AVERAGE EXAM DURATION BY LESSON</Title>
        <Alert
          severity="error"
          sx={{ mt: 2 }}
          action={
            <Button color="inherit" size="small" onClick={load}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      </Paper>
    )
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Title>AVERAGE EXAM DURATION BY LESSON</Title>

      <Box sx={{ display: "flex", gap: 2, mb: 1, mt: 2, flexWrap: "wrap" }}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel shrink>Status</InputLabel>
          <Select<string> value={status} label="Status" onChange={handleStatusChange} displayEmpty notched>
            {STATUS_OPTIONS.map((opt) => (
              <MenuItem key={opt.value === "" ? "__both__" : opt.value} value={opt.value}>{opt.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {data.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No exam duration data available.
        </Typography>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: "bold" }}>Lesson</TableCell>
                <TableCell align="right" sx={{ fontWeight: "bold" }}>Sessions</TableCell>
                <TableCell align="right" sx={{ fontWeight: "bold" }}>Avg Duration</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((row) => (
                <TableRow key={row.lessonId}>
                  <TableCell>{row.lessonName ?? `Unknown (${row.lessonId})`}</TableCell>
                  <TableCell align="right">{row.sessionCount}</TableCell>
                  <TableCell align="right">{formatDuration(row.avgDurationSeconds)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  )
}
